/**
 * Id de sesiune per tab (sessionStorage) - leagă toate întrebările unui
 * vizitator dintr-un tab într-o singură conversație în dashboardul clientului.
 * Aceeași durată de viață ca istoricul chatului (chatHistory.ts): supraviețuiește
 * navigării/refresh-ului în același tab, dispare la închiderea lui.
 *
 * Citirea și crearea sunt DELIBERAT două funcții separate (nu un singur getter
 * cu efect secundar ascuns): ePrivacy art. 5(3) scutește de consimțământ doar
 * stocarea „strict necesară unui serviciu cerut explicit de utilizator" -
 * adică abia după ce vizitatorul a deschis chatul sau a pus o întrebare.
 * `peekChatSessionId` se folosește ÎNAINTE de orice interacțiune (boot,
 * `widget_shown`); `ensureChatSessionId` abia din puncte care urmează unei
 * interacțiuni explicite (deschiderea widgetului, o întrebare, continuarea
 * unei conversații care există deja).
 */

function storageKey(appId: string): string {
  return `aig_sid_${appId}`;
}

function randomId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `s_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  }
}

// fallback în memorie când sessionStorage e indisponibil (private mode etc.) -
// măcar toate întrebările din această încărcare de pagină partajează un id
let memoryId: string | null = null;

/**
 * Id-ul existent al tab-ului, dacă există - FĂRĂ să scrie nimic. `null` dacă
 * acest tab n-a avut încă nicio interacțiune cu chatul (pagină proaspătă sau
 * navigare pe un tab nou). Nu creează niciodată fallback-ul de memorie.
 */
export function peekChatSessionId(appId: string): string | null {
  try {
    return sessionStorage.getItem(storageKey(appId));
  } catch {
    return memoryId;
  }
}

/**
 * Adoptă un session id venit de pe alt subdomeniu declarat al aceluiași bot
 * (handoff prin fragment - chatHandoff.ts), ca firul conversației să fie ACELAȘI
 * pe originea nouă (serverul îl leagă prin `conversations.session_id`). Justificat
 * de ePrivacy 5(3) la fel ca `ensureChatSessionId`: prezența handoff-ului în URL e
 * dovada că vizitatorul a interacționat deja cu chatul (pe originea de plecare).
 */
export function adoptChatSessionId(appId: string, id: string): void {
  try {
    sessionStorage.setItem(storageKey(appId), id);
  } catch {
    memoryId = id;
  }
}

/**
 * Vizitatorul a cerut explicit să o ia de la capăt (butonul de reset din
 * suprafață). Id-ul vechi e ARUNCAT, nu refolosit: conversația de pe server e
 * legată de el (`conversations.session_id`), deci un id nou e singurul mod ca
 * modelul să nu mai vadă ce s-a spus până acum. Cel vechi rămâne la client în
 * dashboard - resetul e uitare pentru vizitator, nu ștergere de date.
 *
 * Mintim id-ul nou pe loc, nu la următoarea întrebare: gestul e o interacțiune
 * explicită, exact temeiul pe care `ensureChatSessionId` are voie să scrie
 * (ePrivacy art. 5(3)), iar o suprafață fără id ar recrea unul oricum.
 */
export function resetChatSessionId(appId: string): string {
  try {
    sessionStorage.removeItem(storageKey(appId));
  } catch {
    memoryId = null;
  }
  memoryId = null;
  return ensureChatSessionId(appId);
}

/** Întoarce id-ul existent al tab-ului sau creează unul nou dacă lipsește. */
export function ensureChatSessionId(appId: string): string {
  try {
    const existing = sessionStorage.getItem(storageKey(appId));
    if (existing) return existing;
    const id = randomId();
    sessionStorage.setItem(storageKey(appId), id);
    return id;
  } catch {
    if (!memoryId) memoryId = randomId();
    return memoryId;
  }
}
